import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CATALOG_ROOT_KEY,
  collectAncestorIds,
  collectDescendantIds,
} from "~/lib/data/hierarchy";
import type { MerchantCategoryRelationship, ServiceItem } from "~/lib/types";

type CategoryEntry = {
  item: ServiceItem;
  vertical: ServiceItem;
  pathLabel: string;
  searchText: string;
};

type CategoryGroup = {
  vertical: ServiceItem;
  entries: CategoryEntry[];
};

type Props = {
  items: ServiceItem[];
  relationships: MerchantCategoryRelationship[];
  className?: string;
};

export const ServiceCategoryFilter = ({
  items,
  relationships,
  className = "",
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const groups = useMemo((): CategoryGroup[] => {
    const root = items.find(i => i.item_key === CATALOG_ROOT_KEY);
    if (!root) return [];

    const rootChildEdges = relationships
      .filter(r => r.related_item_id === root.id)
      .toSorted((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    const categoryItems = items.filter(i => i.item_type === "CATEGORY");
    const itemsById = new Map(categoryItems.map(i => [i.id, i]));

    const buildEntryForDescendant = (
      descendantId: string,
      vertical: ServiceItem,
    ): CategoryEntry | null => {
      if (descendantId === vertical.id) return null;
      const itemForDescendant = itemsById.get(descendantId);
      if (!itemForDescendant) return null;
      const ancestors = collectAncestorIds(itemForDescendant.id, relationships);
      const verticalIdx = ancestors.indexOf(vertical.id);
      const pathSegments =
        verticalIdx > 0
          ? ancestors
              .slice(0, verticalIdx)
              .map(aId => itemsById.get(aId)?.item_name ?? "")
              .filter(Boolean)
              .toReversed()
          : [itemForDescendant.item_name];
      const pathLabel = pathSegments.join(" \u203A ");
      return {
        item: itemForDescendant,
        vertical,
        pathLabel,
        searchText: `${vertical.item_name} ${pathLabel}`.toLowerCase(),
      };
    };

    return rootChildEdges
      .map(edge => itemsById.get(edge.item_id))
      .filter((v): v is ServiceItem => !!v)
      .map(vertical => {
        const descendantIds = collectDescendantIds(vertical.id, relationships);
        const entries: CategoryEntry[] = Array.from(descendantIds)
          .map(id => buildEntryForDescendant(id, vertical))
          .filter((e): e is CategoryEntry => e !== null)
          .toSorted((a, b) => a.pathLabel.localeCompare(b.pathLabel));
        return { vertical, entries };
      })
      .filter(g => g.entries.length > 0);
  }, [items, relationships]);

  const filteredGroups = useMemo((): CategoryGroup[] => {
    if (!filter) return groups;
    const lower = filter.toLowerCase();
    return groups
      .map(g => {
        const verticalMatches = g.vertical.item_name
          .toLowerCase()
          .includes(lower);
        if (verticalMatches) return g;
        const matching = g.entries.filter(e => e.searchText.includes(lower));
        if (matching.length === 0) return null;
        return { ...g, entries: matching };
      })
      .filter((g): g is CategoryGroup => g !== null);
  }, [groups, filter]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectEntry = (entry: CategoryEntry) => {
    navigate(`/services/${entry.item.item_key}`);
    setOpen(false);
    setFilter("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    if (!open) setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  if (groups.length === 0) return null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center rounded-pill border border-border-shell bg-surface-blank focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? "service-category-listbox" : undefined}
          value={filter}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t("ServiceCategoryFilter.placeholder")}
          aria-label={t("ServiceCategoryFilter.placeholder")}
          className="w-full min-w-0 rounded-pill bg-transparent px-4 py-3 font-body text-text-full placeholder-text-light focus:outline-none"
        />
      </div>

      {open && (
        <ul
          id="service-category-listbox"
          role="listbox"
          className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border-shell bg-surface-blank py-1 shadow-content"
        >
          {filteredGroups.length === 0 && (
            <li className="px-4 py-2 text-sm text-text-light">
              {t("ServiceCategoryFilter.no-results")}
            </li>
          )}
          {filteredGroups.map(group => (
            <li key={group.vertical.id}>
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-text-light">
                {group.vertical.item_name}
              </div>
              <ul>
                {group.entries.map(entry => (
                  <li key={entry.item.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => selectEntry(entry)}
                      className="w-full px-6 py-2 text-left text-sm text-text-full hover:bg-surface-elevated"
                    >
                      {entry.pathLabel}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
